import { clsx } from 'clsx'

import { NowButton, PeriodSelector, TimeGrid } from './TimeGrid'
import { generateHours, generateMinutes } from './utils'

interface MobileBottomSheetProps {
  format: '12' | '24'
  minuteStep: number
  selectedHour: number | null
  selectedMinute: number | null
  selectedPeriod: 'AM' | 'PM'
  onHourSelect: (hour: number) => void
  onMinuteSelect: (minute: number) => void
  onPeriodSelect: (period: 'AM' | 'PM') => void
  onNowClick: () => void
  onClose: () => void
}

function MobileTimeGrids(p: Omit<MobileBottomSheetProps, 'onClose'>): JSX.Element {
  return (
    <div className="p-4 space-y-4">
      <TimeGrid
        buttonSizeClass="py-3 text-base rounded-lg text-center"
        gridCols="grid-cols-4"
        items={generateHours(p.format)}
        label="Hour"
        selectedItem={p.selectedHour}
        onSelect={p.onHourSelect}
      />
      <TimeGrid
        buttonSizeClass="py-3 text-base rounded-lg text-center"
        gridCols="grid-cols-4"
        items={generateMinutes(p.minuteStep)}
        label="Minute"
        selectedItem={p.selectedMinute}
        onSelect={p.onMinuteSelect}
      />
      {p.format === '12' && (
        <PeriodSelector
          buttonSizeClass="py-3 text-base font-medium rounded-lg"
          gapClass="gap-3"
          selectedPeriod={p.selectedPeriod}
          onSelect={p.onPeriodSelect}
        />
      )}
      <NowButton
        className="w-full py-3 text-base font-medium rounded-lg text-muted-foreground bg-accent active:bg-accent/80 transition-colors duration-150"
        onClick={p.onNowClick}
      />
    </div>
  )
}

const SHEET_CLASS = clsx(
  'absolute bottom-0 left-0 right-0',
  'bg-card',
  'rounded-t-2xl shadow-xl',
  'animate-slide-up',
  'max-h-[85vh] overflow-y-auto',
)

export function MobileBottomSheet(p: MobileBottomSheetProps): JSX.Element {
  const handleBackdropKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { p.onClose() }
  }
  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50 animate-fade-in"
        role="presentation"
        onClick={p.onClose}
        onKeyDown={handleBackdropKeyDown}
      />
      <div aria-label="Select time" className={SHEET_CLASS} role="dialog">
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-muted-foreground/50 rounded-full" />
        </div>
        <MobileSheetHeader onClose={p.onClose} />
        <MobileTimeGrids {...p} />
        <div className="h-6" />
      </div>
    </div>
  )
}

interface MobileSheetHeaderProps {
  onClose: () => void
}

function MobileSheetHeader({ onClose }: MobileSheetHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between px-4 pb-3 border-b border-border">
      <h2 className="text-lg font-semibold text-foreground">Select Time</h2>
      <button
        className="p-2 -mr-2 text-muted-foreground hover:text-foreground"
        type="button"
        onClick={onClose}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
    </div>
  )
}
